/**
 * Health Intelligence Service
 *
 * Provides proactive health intelligence features including daily briefings,
 * health nudges, risk assessment, medication adherence tracking, vital anomaly
 * detection, and preventive screening reminders.
 *
 * All implementations use realistic mock data for demonstration purposes.
 */

import type {
  DailyHealthBriefing,
  BriefingHighlight,
  HealthNudge,
  RiskScore,
  RiskFactor,
  VitalAnomaly,
  MedicationReminder,
  MedicationAdherence,
  MedicationLogEntry,
  ScreeningReminder,
  HealthInsight,
} from '../types/health-intelligence-types';

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Simulates network delay
 */
const simulateDelay = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Generates a unique identifier
 */
const generateId = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
};

/**
 * Gets time-based greeting
 */
const getTimeBasedGreeting = (name?: string): string => {
  const hour = new Date().getHours();
  let timeOfDay: string;

  if (hour >= 5 && hour < 12) {
    timeOfDay = 'morning';
  } else if (hour >= 12 && hour < 17) {
    timeOfDay = 'afternoon';
  } else {
    timeOfDay = 'evening';
  }

  return name ? `Good ${timeOfDay}, ${name}` : `Good ${timeOfDay}`;
};

/**
 * Gets ISO timestamp for today at specific time
 */
const getTodayAt = (hours: number, minutes: number = 0): string => {
  const date = new Date();
  date.setHours(hours, minutes, 0, 0);
  return date.toISOString();
};

/**
 * Checks if a time has passed today
 */
const hasTimePassed = (hours: number, minutes: number = 0): boolean => {
  const now = new Date();
  const targetTime = new Date();
  targetTime.setHours(hours, minutes, 0, 0);
  return now > targetTime;
};

/**
 * Gets date N days in the future
 */
const getFutureDate = (daysFromNow: number): string => {
  const date = new Date();
  date.setDate(date.getDate() + daysFromNow);
  return date.toISOString().split('T')[0];
};

/**
 * Gets date N days in the past
 */
const getPastDate = (daysAgo: number): string => {
  const date = new Date();
  date.setDate(date.getDate() - daysAgo);
  return date.toISOString().split('T')[0];
};

// ============================================================================
// Mock Data Generators
// ============================================================================

/**
 * Generates mock highlights for daily briefing
 */
const generateMockHighlights = (): BriefingHighlight[] => {
  return [
    {
      type: 'vital-trend',
      title: 'Blood Pressure Improving',
      description:
        'Your average systolic blood pressure has decreased from 138 to 128 mmHg over the past week.',
      severity: 'positive',
      actionLabel: 'View Trends',
      actionPath: '/health-metrics',
      data: {
        metric: 'blood_pressure',
        previousAverage: 138,
        currentAverage: 128,
        change: -10,
      },
    },
    {
      type: 'appointment',
      title: 'Cardiology Follow-up Tomorrow',
      description:
        'Dr. Michael Chen - Cardiology follow-up appointment tomorrow at 10:00 AM.',
      severity: 'info',
      actionLabel: 'View Details',
      actionPath: '/appointments',
      data: {
        appointmentId: 'apt-001',
        doctor: 'Dr. Michael Chen',
        specialty: 'Cardiology',
        time: getTodayAt(10, 0),
      },
    },
    {
      type: 'medication',
      title: 'Excellent Medication Adherence',
      description:
        "You've maintained a 94% adherence rate for Lisinopril over the past month.",
      severity: 'positive',
      actionLabel: 'View Adherence',
      actionPath: '/medications',
      data: {
        medicationName: 'Lisinopril',
        adherenceRate: 94,
        streakDays: 12,
      },
    },
    {
      type: 'achievement',
      title: '7-Day Medication Streak',
      description:
        "Congratulations! You haven't missed a medication dose in 7 days.",
      severity: 'positive',
      data: {
        streakDays: 7,
        achievement: 'medication_streak_7',
      },
    },
  ];
};

/**
 * Generates mock medication reminders for today
 */
const generateMockMedicationReminders = (): MedicationReminder[] => {
  const reminders: MedicationReminder[] = [
    {
      id: generateId(),
      medicationId: 'med-001',
      medicationName: 'Lisinopril',
      dosage: '10mg',
      scheduledTime: getTodayAt(8, 0),
      status: hasTimePassed(8, 0) ? 'taken' : 'pending',
      isOverdue: hasTimePassed(9, 0) && !hasTimePassed(8, 0),
    },
    {
      id: generateId(),
      medicationId: 'med-002',
      medicationName: 'Metformin',
      dosage: '500mg',
      scheduledTime: getTodayAt(8, 0),
      status: hasTimePassed(8, 0) ? 'taken' : 'pending',
      isOverdue: hasTimePassed(9, 0) && !hasTimePassed(8, 0),
    },
    {
      id: generateId(),
      medicationId: 'med-002',
      medicationName: 'Metformin',
      dosage: '500mg',
      scheduledTime: getTodayAt(18, 0),
      status: hasTimePassed(18, 0) ? 'pending' : 'pending',
      isOverdue: hasTimePassed(19, 0),
    },
    {
      id: generateId(),
      medicationId: 'med-003',
      medicationName: 'Atorvastatin',
      dosage: '20mg',
      scheduledTime: getTodayAt(22, 0),
      status: 'pending',
      isOverdue: false,
    },
  ];

  return reminders;
};

/**
 * Generates mock screening reminders
 */
const generateMockScreeningReminders = (): ScreeningReminder[] => {
  return [
    {
      id: generateId(),
      screeningName: 'Colonoscopy',
      description:
        'Preventive screening for colorectal cancer recommended every 10 years for adults over 50.',
      recommendedDate: getPastDate(90),
      isOverdue: true,
      overdueDays: 90,
      basedOn: 'Last screening was 10 years and 3 months ago',
      category: 'condition-based',
      priority: 'overdue',
      actionLabel: 'Schedule Now',
      actionPath: '/schedule-appointment',
    },
    {
      id: generateId(),
      screeningName: 'Annual Physical',
      description:
        'Comprehensive health examination including vital signs, blood work, and general health assessment.',
      recommendedDate: getFutureDate(30),
      isOverdue: false,
      basedOn: 'Recommended annually for your age group',
      category: 'routine',
      priority: 'due-soon',
      actionLabel: 'Schedule Now',
      actionPath: '/schedule-appointment',
    },
  ];
};

/**
 * Generates mock health nudges
 */
const generateMockHealthNudges = (): HealthNudge[] => {
  const now = new Date().toISOString();
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);

  return [
    {
      id: generateId(),
      type: 'trend-alert',
      title: 'Blood Pressure Progress',
      message:
        'Your blood pressure readings are improving! Keep tracking daily to maintain this positive trend.',
      priority: 'medium',
      actionLabel: 'View Trends',
      actionPath: '/health-metrics',
      dismissible: true,
      aiGenerated: true,
      createdAt: now,
      isRead: false,
    },
    {
      id: generateId(),
      type: 'screening-reminder',
      title: 'Overdue Colonoscopy',
      message:
        'Your colonoscopy screening is overdue by 3 months. This is an important preventive screening.',
      priority: 'high',
      actionLabel: 'Schedule Now',
      actionPath: '/schedule-appointment',
      dismissible: false,
      aiGenerated: false,
      createdAt: now,
      isRead: false,
    },
    {
      id: generateId(),
      type: 'goal-progress',
      title: 'Medication Streak Achievement',
      message:
        "Great job! You've maintained a 7-day streak without missing any medications.",
      priority: 'low',
      dismissible: true,
      aiGenerated: true,
      createdAt: now,
      isRead: false,
    },
    {
      id: generateId(),
      type: 'lifestyle-tip',
      title: 'Seasonal Flu Shot',
      message:
        'Consider scheduling a flu shot this season. It\'s especially important for individuals with heart conditions.',
      priority: 'low',
      actionLabel: 'Learn More',
      actionPath: '/preventive-care',
      dismissible: true,
      aiGenerated: true,
      createdAt: now,
      isRead: false,
    },
    {
      id: generateId(),
      type: 'lifestyle-tip',
      title: 'Meal Tracking Reminder',
      message:
        "You haven't logged breakfast today. Consistent meal tracking helps manage blood sugar levels.",
      priority: 'low',
      actionLabel: 'Log Meal',
      actionPath: '/nutrition-tracker',
      dismissible: true,
      expiresAt: tomorrow.toISOString(),
      aiGenerated: false,
      createdAt: now,
      isRead: false,
    },
  ];
};

/**
 * Generates mock risk factors
 */
const generateMockRiskFactors = (): RiskFactor[] => {
  return [
    {
      name: 'Cardiovascular Health',
      score: 28,
      trend: 'improving',
      description:
        'Blood pressure and cholesterol levels are within target ranges. Recent improvements in BP control.',
      actionable: true,
      suggestedAction:
        'Continue current medications and maintain regular monitoring.',
    },
    {
      name: 'Diabetes Management',
      score: 35,
      trend: 'stable',
      description:
        'HbA1c at 6.8% - slightly above target of 6.5%. Blood sugar levels generally well-controlled.',
      actionable: true,
      suggestedAction:
        'Consider dietary adjustments and post-meal walking to improve HbA1c.',
    },
    {
      name: 'Medication Adherence',
      score: 22,
      trend: 'improving',
      description:
        'Overall adherence rate of 92% across all medications. 7-day perfect adherence streak.',
      actionable: true,
      suggestedAction:
        'Keep up the excellent work with medication reminders and tracking.',
    },
    {
      name: 'Preventive Care',
      score: 45,
      trend: 'stable',
      description:
        'Some recommended screenings are overdue. Colonoscopy overdue by 90 days.',
      actionable: true,
      suggestedAction:
        'Schedule overdue colonoscopy and annual physical examination.',
    },
  ];
};

// ============================================================================
// Service Functions
// ============================================================================

/**
 * Retrieves the daily health briefing for the user
 *
 * @param userId - The ID of the user
 * @param userName - The user's first name for the personalised greeting
 * @returns Promise resolving to the daily health briefing
 */
export const getDailyBriefing = async (
  _userId: string,
  userName?: string
): Promise<DailyHealthBriefing> => {
  await simulateDelay(400);

  const now = new Date();
  const highlights = generateMockHighlights();
  const medicationReminders = generateMockMedicationReminders();
  const screeningReminders = generateMockScreeningReminders();
  const nudges = generateMockHealthNudges();
  const riskFactors = generateMockRiskFactors();

  const briefing: DailyHealthBriefing = {
    id: generateId(),
    date: now.toISOString().split('T')[0],
    greeting: getTimeBasedGreeting(userName || undefined),
    summary:
      'Your blood pressure has been trending down this week - great progress! You have 2 medications due today and a cardiology follow-up tomorrow.',
    highlights,
    medicationReminders,
    screeningReminders,
    nudges,
    riskScore: {
      overall: 32,
      category: 'low',
      factors: riskFactors,
      lastUpdated: now.toISOString(),
    },
    generatedAt: now.toISOString(),
  };

  return briefing;
};

/**
 * Retrieves the current risk score assessment for the user
 *
 * @param userId - The ID of the user
 * @returns Promise resolving to the risk score
 */
export const getRiskScore = async (_userId: string): Promise<RiskScore> => {
  await simulateDelay(300);

  const riskFactors = generateMockRiskFactors();

  return {
    overall: 32,
    category: 'low',
    factors: riskFactors,
    lastUpdated: new Date().toISOString(),
  };
};

/**
 * Retrieves detected vital anomalies for the user
 *
 * @param userId - The ID of the user
 * @returns Promise resolving to array of vital anomalies
 */
export const getVitalAnomalies = async (
  _userId: string
): Promise<VitalAnomaly[]> => {
  await simulateDelay(250);

  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);

  return [
    {
      id: generateId(),
      metricName: 'Systolic Blood Pressure',
      value: 145,
      unit: 'mmHg',
      expectedRange: {
        min: 110,
        max: 130,
      },
      deviation: 11.5,
      detectedAt: yesterday.toISOString(),
      severity: 'moderate',
      recommendation:
        'Your blood pressure was elevated yesterday evening. Consider reducing salt intake and monitoring closely. Contact your doctor if readings remain elevated.',
      acknowledged: false,
    },
    {
      id: generateId(),
      metricName: 'Resting Heart Rate',
      value: 92,
      unit: 'bpm',
      expectedRange: {
        min: 60,
        max: 80,
      },
      deviation: 15.0,
      detectedAt: new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000).toISOString(),
      severity: 'mild',
      recommendation:
        'Your resting heart rate was slightly elevated 3 days ago. This could be due to stress, caffeine, or lack of sleep. Monitor and ensure adequate rest.',
      acknowledged: true,
    },
  ];
};

/**
 * Retrieves health nudges for the user
 *
 * @param userId - The ID of the user
 * @returns Promise resolving to array of health nudges
 */
export const getHealthNudges = async (_userId: string): Promise<HealthNudge[]> => {
  await simulateDelay(200);
  return generateMockHealthNudges();
};

/**
 * Dismisses a health nudge
 *
 * @param nudgeId - The ID of the nudge to dismiss
 * @returns Promise that resolves when the nudge is dismissed
 */
export const dismissNudge = async (nudgeId: string): Promise<void> => {
  await simulateDelay(200);
  // In a real implementation, this would update the backend
  console.log(`Dismissed nudge: ${nudgeId}`);
};

/**
 * Snoozes a health nudge for a specified duration
 *
 * @param nudgeId - The ID of the nudge to snooze
 * @param hours - Number of hours to snooze (default: 24)
 * @returns Promise that resolves when the nudge is snoozed
 */
export const snoozeNudge = async (
  nudgeId: string,
  hours: number = 24
): Promise<void> => {
  await simulateDelay(200);
  // In a real implementation, this would update the backend
  console.log(`Snoozed nudge ${nudgeId} for ${hours} hours`);
};

/**
 * Retrieves screening reminders for the user
 *
 * @param userId - The ID of the user
 * @returns Promise resolving to array of screening reminders
 */
export const getScreeningReminders = async (
  _userId: string
): Promise<ScreeningReminder[]> => {
  await simulateDelay(300);

  return [
    {
      id: generateId(),
      screeningName: 'Colonoscopy',
      description:
        'Preventive screening for colorectal cancer recommended every 10 years for adults over 50.',
      recommendedDate: getPastDate(90),
      isOverdue: true,
      overdueDays: 90,
      basedOn: 'Last screening was 10 years and 3 months ago',
      category: 'condition-based',
      priority: 'overdue',
      actionLabel: 'Schedule Now',
      actionPath: '/schedule-appointment',
    },
    {
      id: generateId(),
      screeningName: 'Annual Physical',
      description:
        'Comprehensive health examination including vital signs, blood work, and general health assessment.',
      recommendedDate: getFutureDate(30),
      isOverdue: false,
      basedOn: 'Recommended annually for your age group',
      category: 'routine',
      priority: 'due-soon',
      actionLabel: 'Schedule Now',
      actionPath: '/schedule-appointment',
    },
    {
      id: generateId(),
      screeningName: 'Diabetic Eye Exam',
      description:
        'Comprehensive eye examination to check for diabetic retinopathy and other complications.',
      recommendedDate: getFutureDate(45),
      isOverdue: false,
      basedOn: 'Recommended annually for patients with diabetes',
      category: 'condition-based',
      priority: 'recommended',
      actionLabel: 'Schedule Now',
      actionPath: '/schedule-appointment',
    },
    {
      id: generateId(),
      screeningName: 'Dental Cleaning',
      description:
        'Professional teeth cleaning and oral health examination.',
      recommendedDate: getFutureDate(15),
      isOverdue: false,
      basedOn: 'Recommended every 6 months',
      category: 'routine',
      priority: 'due-soon',
      actionLabel: 'Schedule Now',
      actionPath: '/schedule-appointment',
    },
  ];
};

/**
 * Retrieves medication reminders for today
 *
 * @param userId - The ID of the user
 * @returns Promise resolving to array of medication reminders
 */
export const getMedicationReminders = async (
  _userId: string
): Promise<MedicationReminder[]> => {
  await simulateDelay(200);
  return generateMockMedicationReminders();
};

/**
 * Retrieves medication adherence statistics
 *
 * @param userId - The ID of the user
 * @returns Promise resolving to array of medication adherence data
 */
export const getMedicationAdherence = async (
  _userId: string
): Promise<MedicationAdherence[]> => {
  await simulateDelay(300);

  return [
    {
      medicationId: 'med-001',
      medicationName: 'Lisinopril',
      adherenceRate: 94,
      missedDoses: 2,
      totalDoses: 30,
      period: 'month',
      trend: 'improving',
      streakDays: 12,
    },
    {
      medicationId: 'med-002',
      medicationName: 'Metformin',
      adherenceRate: 88,
      missedDoses: 7,
      totalDoses: 60,
      period: 'month',
      trend: 'stable',
      streakDays: 5,
    },
    {
      medicationId: 'med-003',
      medicationName: 'Atorvastatin',
      adherenceRate: 96,
      missedDoses: 1,
      totalDoses: 30,
      period: 'month',
      trend: 'improving',
      streakDays: 18,
    },
  ];
};

/**
 * Logs a medication action (taken, skipped, snoozed)
 *
 * @param entry - The medication log entry (without ID)
 * @returns Promise resolving to the complete log entry with generated ID
 */
export const logMedication = async (
  entry: Omit<MedicationLogEntry, 'id'>
): Promise<MedicationLogEntry> => {
  await simulateDelay(200);

  const logEntry: MedicationLogEntry = {
    id: generateId(),
    ...entry,
  };

  // In a real implementation, this would send to backend
  console.log('Logged medication:', logEntry);

  return logEntry;
};

/**
 * Retrieves AI-generated health insights for the user
 *
 * @param userId - The ID of the user
 * @returns Promise resolving to array of health insights
 */
export const getHealthInsights = async (
  _userId: string
): Promise<HealthInsight[]> => {
  await simulateDelay(350);

  const now = new Date();
  const expiresIn7Days = new Date(now);
  expiresIn7Days.setDate(expiresIn7Days.getDate() + 7);

  return [
    {
      id: generateId(),
      category: 'vitals',
      severity: 'info',
      title: 'Resting Heart Rate Improvement',
      body: 'Your resting heart rate has decreased by 4 bpm over the past month, from an average of 76 bpm to 72 bpm. This improvement suggests enhanced cardiovascular fitness and is associated with better overall heart health.',
      sparklineData: [76, 75, 74, 75, 73, 74, 73, 72, 72, 71, 72, 72],
      relatedActionLabel: 'View Heart Rate Trends',
      relatedActionPath: '/health-metrics',
      generatedAt: now.toISOString(),
      isRead: false,
      isBookmarked: false,
    },
    {
      id: generateId(),
      category: 'medications',
      severity: 'suggestion',
      title: 'Excellent Medication Adherence',
      body: 'Your medication adherence is currently at 92%, which is above the 90% threshold associated with optimal health outcomes. Research shows that maintaining this level of adherence significantly reduces the risk of cardiovascular complications.',
      relatedActionLabel: 'View Adherence Details',
      relatedActionPath: '/medications',
      generatedAt: now.toISOString(),
      isRead: false,
      isBookmarked: false,
    },
    {
      id: generateId(),
      category: 'screenings',
      severity: 'warning',
      title: 'Overdue Colonoscopy Screening',
      body: "You're currently 90 days overdue for a colonoscopy screening. This preventive screening is crucial for early detection of colorectal cancer, which has a 90% survival rate when caught early. We strongly recommend scheduling this appointment soon.",
      relatedActionLabel: 'Schedule Colonoscopy',
      relatedActionPath: '/schedule-appointment',
      generatedAt: now.toISOString(),
      expiresAt: expiresIn7Days.toISOString(),
      isRead: false,
      isBookmarked: false,
    },
    {
      id: generateId(),
      category: 'wellness',
      severity: 'suggestion',
      title: 'Post-Dinner Walking Suggestion',
      body: "Based on your activity patterns and blood sugar readings, adding a 15-minute walk after dinner could help improve your glucose management. Evening walks have been shown to reduce post-meal blood sugar spikes by up to 20% in individuals with Type 2 diabetes.",
      sparklineData: [145, 148, 142, 151, 147, 143, 140],
      relatedActionLabel: 'Set Walking Goal',
      relatedActionPath: '/activity-tracker',
      generatedAt: now.toISOString(),
      isRead: false,
      isBookmarked: false,
    },
  ];
};
